import type { SeoMetrics } from "../../types/seo";

interface TierInfo {
	label: string;
	blurb: string;
	chip: string;
}

const TIERS: Record<string, TierInfo> = {
	drop: {
		label: "Drop",
		blurb:
			"The position fell while the page still earns impressions. Highest priority, and the one tier with no daily budget — every drop is queued.",
		chip: "bg-rose-50 text-rose-700",
	},
	regression: {
		label: "Regression",
		blurb:
			"The position fell shortly after we rewrote the page ourselves. These always stop for a person, whatever the schedule says, because an automated write is what made it worse.",
		chip: "bg-rose-100 text-rose-800",
	},
	focus: {
		label: "Focus",
		blurb:
			"The first pass over the focus set — the top pages by 28-day impressions that have never been optimised. Most impressions first.",
		chip: "bg-violet-50 text-violet-700",
	},
	striking: {
		label: "Striking distance",
		blurb:
			"Already optimised once, now sitting at position 4–20. Ordered by how far the snippet under-earns the traffic it is already shown for.",
		chip: "bg-amber-50 text-amber-700",
	},
	competitor: {
		label: "Competitor gap",
		blurb:
			"A search for one exact product where PriceRunner ranks in the top 3 and our product page ranks far below. The page is rewritten against that search. Ordered by search volume.",
		chip: "bg-orange-50 text-orange-700",
	},
	demand: {
		label: "Demand",
		blurb:
			"Already optimised, earns impressions, but ranks below position 20. Ordered by impressions.",
		chip: "bg-sky-50 text-sky-700",
	},
	cold: {
		label: "Cold",
		blurb:
			"No impressions in 90 days. These are written by the cold pass in backend-rust rather than by a flow, and a flow that selects this tier cannot be scheduled.",
		chip: "bg-slate-100 text-slate-600",
	},
	manual: {
		label: "Manual",
		blurb:
			"Someone pressed Optimize now on a product. Jumps ahead of everything but drops and regressions.",
		chip: "bg-emerald-50 text-emerald-700",
	},
	rollback: {
		label: "Rollback",
		blurb:
			"A previous version's text written back as a new version. Nothing was deleted to make it.",
		chip: "bg-slate-100 text-slate-600",
	},
};

export const TIER_ORDER = [
	"drop",
	"regression",
	"focus",
	"striking",
	"competitor",
	"demand",
	"cold",
	"manual",
];

export const tierInfo = (tier: string): TierInfo =>
	TIERS[tier] ?? {
		label: tier,
		blurb: "A tier this page does not know about yet.",
		chip: "bg-slate-100 text-slate-600",
	};

export const TierChip = ({ tier }: { tier: string }) => {
	const info = tierInfo(tier);
	return (
		<span
			title={info.blurb}
			className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${info.chip}`}
		>
			{info.label}
		</span>
	);
};

const SERP_CLASS: Record<string, { label: string; chip: string; hint: string }> =
	{
		clean: {
			label: "Clean",
			chip: "bg-emerald-50 text-emerald-700",
			hint: "Nothing above the organic results. About 34% of clicks go to position 1 here, so a rewrite is worth the most.",
		},
		shopping: {
			label: "Shopping",
			chip: "bg-amber-50 text-amber-700",
			hint: "A Shopping unit sits above. Position 1 earns roughly 14%, so most of the click-through gap is out of reach of the title.",
		},
		aggregator: {
			label: "Grid",
			chip: "bg-amber-50 text-amber-700",
			hint: "The Prisportaler comparison grid sits above. Winning it is feed work, not title work.",
		},
		aio: {
			label: "AI Overview",
			chip: "bg-rose-50 text-rose-700",
			hint: "An AI Overview sits above, taking click-through to around 10% and lower on commercial queries.",
		},
		mixed: {
			label: "Mixed",
			chip: "bg-rose-50 text-rose-700",
			hint: "More than one of Shopping, the grid and an AI Overview. The worst case for a title rewrite.",
		},
	};

export const SerpClassChip = ({ serpClass }: { serpClass: string | null }) => {
	if (!serpClass) {
		return (
			<span
				title="The monthly DataForSEO sweep has not classified this product's keywords yet. Unknown is not the same as clean."
				className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-400"
			>
				—
			</span>
		);
	}
	const info = SERP_CLASS[serpClass];
	return (
		<span
			title={info?.hint}
			className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
				info?.chip ?? "bg-slate-100 text-slate-600"
			}`}
		>
			{info?.label ?? serpClass}
		</span>
	);
};

const QUEUE_STATUS: Record<string, string> = {
	queued: "bg-sky-50 text-sky-700",
	claimed: "bg-violet-50 text-violet-700",
	done: "bg-emerald-50 text-emerald-700",
	skipped: "bg-slate-100 text-slate-500",
	expired: "bg-slate-100 text-slate-400",
};

const QUEUE_STATUS_HINT: Record<string, string> = {
	queued: "Waiting for the next run that reads this tier.",
	claimed: "A run has taken this row and is working on it.",
	done: "Written. The history row carries what was written and what it looked like before.",
	skipped: "Set aside by a person. Promote puts it back in the queue.",
	expired: "Left unclaimed long enough that the refill dropped it.",
};

export const QueueStatusChip = ({ status }: { status: string }) => (
	<span
		title={QUEUE_STATUS_HINT[status]}
		className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
			QUEUE_STATUS[status] ?? "bg-slate-100 text-slate-600"
		}`}
	>
		{status}
	</span>
);

const OUTCOMES: Record<string, { label: string; chip: string; blurb: string }> =
	{
		pending: {
			label: "Not measured yet",
			chip: "bg-slate-100 text-slate-500",
			blurb:
				"The first measurement is taken 28 days after the write, so there is nothing to compare against until then.",
		},
		improved: {
			label: "Improved",
			chip: "bg-emerald-50 text-emerald-700",
			blurb:
				"Position better by at least 1.0, or click-through up by at least a quarter at roughly the same position.",
		},
		flat: {
			label: "Flat",
			chip: "bg-slate-100 text-slate-600",
			blurb: "Measured, and neither better nor worse by enough to call it.",
		},
		regressed: {
			label: "Regressed",
			chip: "bg-rose-100 text-rose-800",
			blurb:
				"Position worse by at least 2.0, or clicks down by 40 % at steady impressions. This is the number that stops a rollout — the product is queued again as a regression and can be rolled back.",
		},
		unmeasurable: {
			label: "Too little traffic",
			chip: "bg-slate-100 text-slate-400",
			blurb:
				"Fewer than 30 impressions in one of the two windows, so the comparison would be noise.",
		},
	};

export const outcomeInfo = (outcome: string) =>
	OUTCOMES[outcome] ?? {
		label: outcome,
		chip: "bg-slate-100 text-slate-600",
		blurb: "",
	};

export const OutcomeChip = ({ outcome }: { outcome: string }) => {
	const info = outcomeInfo(outcome);
	return (
		<span
			title={info.blurb}
			className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${info.chip}`}
		>
			{info.label}
		</span>
	);
};

export const pendingExplanation = (writtenAt: string | null): string => {
	if (!writtenAt) return OUTCOMES.pending.blurb;
	const due = new Date(writtenAt);
	if (Number.isNaN(due.getTime())) return OUTCOMES.pending.blurb;
	due.setDate(due.getDate() + 31);
	const passed = due.getTime() < Date.now();
	return passed
		? `The 28 days are up as of ${due.toLocaleDateString("da-DK")}. The next track_positions run is what stamps this one.`
		: `Measured 28 days after the write, so around ${due.toLocaleDateString("da-DK")}. Nothing is wrong with it before then.`;
};

const num = (value: unknown): number | null =>
	typeof value === "number" && Number.isFinite(value) ? value : null;

const text = (value: unknown): string | null =>
	typeof value === "string" && value.trim() !== "" ? value : null;

const pos = (value: unknown): string | null => {
	const n = num(value);
	return n === null ? null : n.toFixed(1);
};

const pct = (value: unknown): string | null => {
	const n = num(value);
	return n === null ? null : `${(n * 100).toFixed(2)} %`;
};

const count = (value: unknown): string | null => {
	const n = num(value);
	return n === null ? null : n.toLocaleString("da-DK");
};

const KNOWN = new Set([
	"baselinePos",
	"currentPos",
	"impr7d",
	"impr28d",
	"clicks7d",
	"clicks28d",
	"topQuery",
	"rank",
	"pos28d",
	"seoVersion",
	"expectedCtr",
	"actualCtr",
	"ctrGap",
	"merchantCount",
	"popularityScore",
	"source",
	"requestedBy",
	"requestedAt",
	"outcome",
	"historyId",
	"before",
	"after",
]);

export const summariseReason = (
	tier: string,
	reason: Record<string, unknown> | null,
): string[] => {
	if (!reason || Object.keys(reason).length === 0) {
		return tier === "cold"
			? ["No search demand on record — picked by offer count."]
			: ["No evidence was recorded with this row."];
	}

	const parts: string[] = [];

	const base = pos(reason.baselinePos);
	const now = pos(reason.currentPos);
	if (base && now) parts.push(`position ${base} → ${now}`);
	else if (now) parts.push(`position ${now}`);

	const rank = count(reason.rank);
	if (rank) parts.push(`focus rank #${rank}`);

	const pos28 = pos(reason.pos28d);
	if (pos28 && !now) parts.push(`position ${pos28} over 28 days`);

	const impr7 = count(reason.impr7d);
	const impr28 = count(reason.impr28d);
	if (impr7 && impr28)
		parts.push(`${impr7} impressions in 7 d, ${impr28} in 28`);
	else if (impr28) parts.push(`${impr28} impressions in 28 days`);
	else if (impr7) parts.push(`${impr7} impressions in 7 days`);

	const clicks7 = count(reason.clicks7d);
	const clicks28 = count(reason.clicks28d);
	if (clicks7 && clicks28)
		parts.push(`${clicks7} clicks in 7 d, ${clicks28} in 28`);
	else if (clicks28) parts.push(`${clicks28} clicks in 28 days`);

	const actual = pct(reason.actualCtr);
	const expected = pct(reason.expectedCtr);
	if (actual && expected)
		parts.push(
			`click-through ${actual} against ${expected} expected at that position`,
		);
	const gap = num(reason.ctrGap);
	if (gap !== null)
		parts.push(
			gap >= 0
				? `about ${Math.round(gap).toLocaleString("da-DK")} clicks a month the snippet is not earning`
				: `the snippet already out-earns its position by about ${Math.round(-gap).toLocaleString("da-DK")} clicks a month`,
		);

	const merchants = count(reason.merchantCount);
	if (merchants) parts.push(`${merchants} comparable offers`);
	const popularity = num(reason.popularityScore);
	if (popularity !== null)
		parts.push(`popularity ${popularity.toLocaleString("da-DK")}`);

	const regressed = text(reason.outcome) === "regressed";
	const version = num(reason.seoVersion);
	if (version !== null && !regressed)
		parts.push(
			version === 0
				? "never optimised"
				: `optimised ${version} time${version === 1 ? "" : "s"} so far`,
		);

	const query = text(reason.topQuery);
	if (query) parts.push(`top query “${query}”`);

	if (regressed) {
		parts.unshift(
			version === null
				? "the measurement of our own last write came back regressed"
				: `version ${version} measured as regressed`,
		);
	}

	if (text(reason.source) === "admin") {
		const by = text(reason.requestedBy);
		parts.push(
			by ? `queued by hand by ${by}` : "queued by hand from the admin",
		);
	}

	for (const [key, value] of Object.entries(reason)) {
		if (KNOWN.has(key)) continue;
		if (value === null || typeof value === "object") continue;
		parts.push(`${key} ${String(value)}`);
	}

	return parts.length > 0 ? parts : ["No evidence was recorded with this row."];
};

export const metricCells = (
	metrics: SeoMetrics | null,
): Array<{ label: string; value: string }> => {
	if (!metrics) return [];
	const impr = num(metrics.impr28);
	const clicks = num(metrics.clicks28);
	const ctr = num(metrics.ctr28);
	const position = num(metrics.pos28);
	return [
		{
			label: "Impressions",
			value: impr === null ? "—" : impr.toLocaleString("da-DK"),
		},
		{
			label: "Clicks",
			value: clicks === null ? "—" : clicks.toLocaleString("da-DK"),
		},
		{ label: "CTR", value: ctr === null ? "—" : `${(ctr * 100).toFixed(2)} %` },
		{ label: "Position", value: position === null ? "—" : position.toFixed(1) },
	];
};

const WRITTEN_BY: Record<string, string> = {
	flow: "a flow",
	cold_pass: "the cold pass",
	rollback: "a rollback",
};

export const writtenByLabel = (writtenBy: string): string =>
	WRITTEN_BY[writtenBy] ?? writtenBy;
